import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Users, Trash2, Store, Shield, User, Eye, EyeOff, Key, Crown, UserCog, MapPin, Headphones, Percent } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CONFIG = {
  master_admin: { label: 'Master Admin', color: 'text-gold', bgColor: 'bg-gold/10', icon: Crown },
  admin: { label: 'Admin Sucursal', color: 'text-blue-400', bgColor: 'bg-blue-400/10', icon: Shield },
  zone_manager: { label: 'Gerente de Zona', color: 'text-purple-400', bgColor: 'bg-purple-400/10', icon: MapPin },
  recepcionista: { label: 'Recepcionista', color: 'text-pink-400', bgColor: 'bg-pink-400/10', icon: Headphones },
  manager: { label: 'Encargado', color: 'text-zinc-400', bgColor: 'bg-zinc-800', icon: UserCog },
};

export default function AdminUsers() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'manager',
    branch_id: '',
    branch_ids: [],
    product_commission_percentage: 10,
  });

  const isMasterAdmin = currentUser?.role === 'master_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Zone managers are assigned to zones (not branches) via AdminZones — branch optional
    // Receptionists need at least one branch from the multi-select
    if (formData.role === 'recepcionista') {
      if (!formData.branch_ids || formData.branch_ids.length === 0) {
        toast.error('Selecciona al menos una sucursal para la recepcionista');
        return;
      }
    } else if (formData.role !== 'zone_manager' && !formData.branch_id) {
      toast.error('Selecciona una sucursal');
      return;
    }

    try {
      const payload = { ...formData };
      if (payload.role === 'zone_manager') {
        payload.branch_id = null;
        payload.branch_ids = null;
      } else if (payload.role === 'recepcionista') {
        payload.branch_id = null;
        payload.product_commission_percentage = Number(payload.product_commission_percentage) || 0;
      } else {
        payload.branch_ids = null;
        payload.product_commission_percentage = null;
      }
      await axios.post(`${API}/users`, payload, getAuthHeaders());
      const roleLabel = ROLE_CONFIG[formData.role]?.label || 'Usuario';
      toast.success(`${roleLabel} creado exitosamente`);
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      password: '',
      role: 'manager',
      branch_id: '',
      branch_ids: [],
      product_commission_percentage: 10,
    });
    setShowPassword(false);
  };

  const toggleBranchInSelection = (branchId) => {
    setFormData(prev => {
      const exists = prev.branch_ids.includes(branchId);
      const next = exists ? prev.branch_ids.filter(b => b !== branchId) : [...prev.branch_ids, branchId];
      return { ...prev, branch_ids: next };
    });
  };

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/users/${id}`, getAuthHeaders());
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Filter users - Master Admin sees all, Admin sees only their branch
  const filteredUsers = users.filter(u => {
    if (u.role === 'master_admin') return true; // Always show master admin
    if (isMasterAdmin) return true; // Master admin sees all
    return u.branch_id === currentUser?.branch_id; // Others see only their branch
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Usuarios</h1>
            <p className="text-zinc-500 mt-1">
              {isMasterAdmin ? 'Gestiona admins de sucursal y encargados' : 'Gestiona los encargados de tu sucursal'}
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gold text-black hover:bg-gold-dark"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  Nuevo Usuario
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Role Selector - Only for Master Admin */}
                {isMasterAdmin && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Tipo de Usuario</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="manager" className="text-white" data-testid="role-option-manager">
                          <div className="flex items-center gap-2">
                            <UserCog className="w-4 h-4 text-zinc-400" />
                            <span>Encargado (POS)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="admin" className="text-white" data-testid="role-option-admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-400" />
                            <span>Admin de Sucursal</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="zone_manager" className="text-white" data-testid="role-option-zone-manager">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-purple-400" />
                            <span>Gerente de Zona</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="recepcionista" className="text-white" data-testid="role-option-recepcionista">
                          <div className="flex items-center gap-2">
                            <Headphones className="w-4 h-4 text-pink-400" />
                            <span>Recepcionista</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      {formData.role === 'admin' && 'Podrá ver reportes, gestionar barberos y configuración de su sucursal'}
                      {formData.role === 'manager' && 'Solo podrá usar el punto de venta (POS)'}
                      {formData.role === 'zone_manager' && 'Supervisará varias sucursales desde el Dashboard Gerencial. Asigna la zona desde "Zonas Gerenciales".'}
                      {formData.role === 'recepcionista' && 'Puede usar el POS, abrir/cerrar caja, acceder a Cortes del Día y Lealtad. Recibe comisión solo por venta de productos.'}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre Completo</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Juan Pérez"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="user-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Usuario</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                    placeholder="juanperez"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="user-username-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Contraseña</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="bg-zinc-800 border-zinc-700 text-white pr-10"
                      data-testid="user-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {formData.role !== 'zone_manager' && formData.role !== 'recepcionista' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Sucursal</Label>
                    <Select
                      value={formData.branch_id}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="user-branch-select">
                        <SelectValue placeholder="Seleccionar sucursal" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id} className="text-white">
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {formData.role === 'recepcionista' && (
                  <>
                    <div className="space-y-2" data-testid="recep-branches-multi">
                      <Label className="text-zinc-400">Sucursales (selecciona una o varias)</Label>
                      <div className="flex flex-wrap gap-2">
                        {branches.map((branch) => {
                          const selected = formData.branch_ids.includes(branch.id);
                          return (
                            <button
                              type="button"
                              key={branch.id}
                              onClick={() => toggleBranchInSelection(branch.id)}
                              className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                                selected
                                  ? 'bg-pink-500/20 border-pink-500/50 text-pink-300'
                                  : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white'
                              }`}
                              data-testid={`recep-branch-chip-${branch.id}`}
                            >
                              <Store className="w-3 h-3 inline mr-1" />
                              {branch.name}
                            </button>
                          );
                        })}
                      </div>
                      {formData.branch_ids.length === 0 && (
                        <p className="text-xs text-zinc-600">Selecciona al menos una sucursal</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 flex items-center gap-1">
                        <Percent className="w-3 h-3" />
                        Comisión por producto (%)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={formData.product_commission_percentage}
                        onChange={(e) => setFormData({ ...formData, product_commission_percentage: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="recep-commission-input"
                      />
                      <p className="text-xs text-zinc-500">Porcentaje que la recepcionista recibirá por cada producto vendido en el POS.</p>
                    </div>
                  </>
                )}
                {formData.role === 'zone_manager' && (
                  <div className="p-3 bg-purple-500/5 border border-purple-900/30 rounded-sm" data-testid="zone-manager-hint">
                    <p className="text-xs text-purple-300 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Después de crear el usuario, asígnale una zona en <span className="font-semibold">Zonas Gerenciales</span>.
                    </p>
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                    data-testid="user-submit-btn"
                  >
                    Crear Usuario
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No hay usuarios registrados</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((user) => {
              const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.manager;
              const RoleIcon = roleConfig.icon;
              
              return (
                <Card 
                  key={user.id} 
                  className="bg-zinc-900/50 border-zinc-800 p-4 hover:border-zinc-700 transition-colors"
                  data-testid={`user-card-${user.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full ${roleConfig.bgColor} flex items-center justify-center`}>
                        <RoleIcon className={`w-5 h-5 ${roleConfig.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-white">{user.name}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs ${roleConfig.bgColor} ${roleConfig.color}`}>
                            {roleConfig.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1">
                          <span className="flex items-center gap-1 text-zinc-400">
                            <User className="w-3 h-3" />
                            @{user.username}
                          </span>
                          {user.branch_name && (
                            <span className="flex items-center gap-1">
                              <Store className="w-3 h-3" />
                              {user.branch_name}
                            </span>
                          )}
                          {user.branch_names && user.branch_names.length > 0 && (
                            <span className="flex items-center gap-1 text-pink-300">
                              <Store className="w-3 h-3" />
                              {user.branch_names.join(' · ')}
                            </span>
                          )}
                          {user.product_commission_percentage != null && user.role === 'recepcionista' && (
                            <span className="flex items-center gap-1 text-pink-300">
                              <Percent className="w-3 h-3" />
                              {user.product_commission_percentage}% prod.
                            </span>
                          )}
                        </div>
                        {/* Credentials display - only for admin and manager roles */}
                        {user.role !== 'master_admin' && user.plain_password && (
                          <div className="flex items-center gap-2 mt-2 p-2 bg-zinc-800/50 rounded-sm">
                            <Key className="w-3 h-3 text-zinc-500" />
                            <span className="text-xs text-zinc-500">Contraseña:</span>
                            <span className="text-xs text-zinc-300 font-mono">
                              {visiblePasswords[user.id] ? user.plain_password : '••••••••'}
                            </span>
                            <button
                              onClick={() => togglePasswordVisibility(user.id)}
                              className="text-zinc-500 hover:text-gold transition-colors ml-1"
                            >
                              {visiblePasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {user.id !== currentUser?.id && user.role !== 'master_admin' && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="w-8 h-8 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
